import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { analysisApi, type CrossAnalysisParams } from '@/api/analysis';
import { Select, type SelectOption } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Download,
  RefreshCw,
  Table2,
  HelpCircle,
  Lightbulb,
  TrendingUp,
  BarChart3,
  Calendar,
  Users,
  Zap,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';
import type {
  CrossAnalysisResponse,
  CrossAnalysisDimension,
  CrossAnalysisMetric,
} from '@/types/data';

// プリセットテンプレート
interface PresetTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  dimension1: CrossAnalysisDimension;
  dimension2: CrossAnalysisDimension;
  metric: CrossAnalysisMetric;
}

const presetTemplates: PresetTemplate[] = [
  {
    id: 'monthly-category',
    name: '月別×カテゴリ',
    description: '月ごとに各カテゴリ（登録、タップ、予約等）がどう推移しているか',
    icon: <Calendar className="w-5 h-5" />,
    dimension1: 'month',
    dimension2: 'category',
    metric: 'daily_sum',
  },
  {
    id: 'account-category',
    name: 'アカウント別×カテゴリ',
    description: '各アカウント（LINE、メール等）ごとのパフォーマンス比較',
    icon: <Users className="w-5 h-5" />,
    dimension1: 'account',
    dimension2: 'category',
    metric: 'daily_sum',
  },
  {
    id: 'scenario-channel',
    name: 'シナリオ別×チャネル',
    description: 'どの流入経路からLINE/メールに来ているか',
    icon: <TrendingUp className="w-5 h-5" />,
    dimension1: 'scenario',
    dimension2: 'channel',
    metric: 'daily_sum',
  },
  {
    id: 'weekly-account',
    name: '週別×アカウント',
    description: '週ごとのアカウント別パフォーマンス推移',
    icon: <BarChart3 className="w-5 h-5" />,
    dimension1: 'week',
    dimension2: 'account',
    metric: 'daily_sum',
  },
];

// 軸のオプション（詳細説明付き）
const dimensionOptions: (SelectOption & { description: string })[] = [
  { value: 'category', label: 'カテゴリ', description: '登録、タップ、予約など行動の種類' },
  { value: 'account', label: 'アカウント', description: 'LINE、メールなどのアカウント' },
  { value: 'month', label: '月', description: '1月、2月...など月単位' },
  { value: 'week', label: '週', description: '第1週、第2週...など週単位' },
  { value: 'scenario', label: 'シナリオ', description: 'オプトイン、個別案内など流入経路' },
  { value: 'channel', label: 'チャネル', description: 'LINE か メール か' },
];

// 指標のオプション（詳細説明付き）
const metricOptions: (SelectOption & { description: string })[] = [
  { value: 'daily_sum', label: '期間内の合計', description: '選んだ期間内の数値を合計' },
  { value: 'cumulative_latest', label: '累計（最新時点）', description: '期間終了時点での累計値' },
  { value: 'count', label: 'データ件数', description: 'データポイントの数' },
];

// 軸の日本語名マッピング
const dimensionLabels: Record<string, string> = {
  category: 'カテゴリ',
  account: 'アカウント',
  month: '月',
  week: '週',
  scenario: 'シナリオ',
  channel: 'チャネル',
};

export default function CrossAnalysis() {
  const { accessibleCompanies } = useAuthStore();

  // 状態管理
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [dimension1, setDimension1] = useState<CrossAnalysisDimension>('month');
  const [dimension2, setDimension2] = useState<CrossAnalysisDimension>('category');
  const [metric, setMetric] = useState<CrossAnalysisMetric>('daily_sum');
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [showHelp, setShowHelp] = useState(false);
  const [showCustomSettings, setShowCustomSettings] = useState(false);

  const [data, setData] = useState<CrossAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 初回法人設定
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // 法人オプション
  const companyOptions: SelectOption[] = accessibleCompanies.map((c) => ({
    value: c.id,
    label: c.display_name || c.name,
  }));

  // dimension2のオプション（dimension1と同じものは除外）
  const dimension2Options = useMemo(() => {
    return dimensionOptions.filter((opt) => opt.value !== dimension1);
  }, [dimension1]);

  // dimension1が変更されたときにdimension2を調整
  useEffect(() => {
    if (dimension1 === dimension2) {
      const newDim2 = dimensionOptions.find((opt) => opt.value !== dimension1);
      if (newDim2) {
        setDimension2(newDim2.value as CrossAnalysisDimension);
      }
    }
  }, [dimension1, dimension2]);

  // データ取得（パラメータを直接指定可能）
  const fetchData = async (
    overrides?: {
      dim1?: CrossAnalysisDimension;
      dim2?: CrossAnalysisDimension;
      met?: CrossAnalysisMetric;
    }
  ) => {
    if (!selectedCompanyId) return;

    setLoading(true);
    setError(null);

    try {
      const params: CrossAnalysisParams = {
        company_id: selectedCompanyId,
        dimension1: overrides?.dim1 ?? dimension1,
        dimension2: overrides?.dim2 ?? dimension2,
        metric: overrides?.met ?? metric,
        start_date: startDate,
        end_date: endDate,
      };

      const result = await analysisApi.getCrossAnalysis(params);
      setData(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // プリセット適用（選択と同時に分析実行）
  const applyPreset = (preset: PresetTemplate) => {
    setDimension1(preset.dimension1);
    setDimension2(preset.dimension2);
    setMetric(preset.metric);
    setShowCustomSettings(false);
    // 即座に分析実行（state更新を待たずにプリセット値で実行）
    fetchData({
      dim1: preset.dimension1,
      dim2: preset.dimension2,
      met: preset.metric,
    });
  };

  // 初回データ取得
  useEffect(() => {
    if (selectedCompanyId) {
      fetchData();
    }
  }, [selectedCompanyId]);

  // データをマトリックス形式に変換
  const matrixData = useMemo(() => {
    if (!data) return null;

    const matrix: Record<string, Record<string, number>> = {};

    // 行を初期化
    for (const row of data.rows) {
      matrix[row] = {};
      for (const col of data.columns) {
        matrix[row][col] = 0;
      }
    }

    // データを埋める
    for (const cell of data.data) {
      if (matrix[cell.row_value]) {
        matrix[cell.row_value][cell.col_value] = cell.value;
      }
    }

    return matrix;
  }, [data]);

  // 最大値を見つける（ハイライト用）
  const maxValue = useMemo(() => {
    if (!data) return 0;
    return Math.max(...data.data.map(cell => cell.value), 0);
  }, [data]);

  // セルの背景色を計算
  const getCellBackground = (value: number): string => {
    if (maxValue === 0 || value === 0) return '';
    const intensity = Math.min(value / maxValue, 1);
    const alpha = Math.floor(intensity * 40 + 10); // 10-50の範囲
    return `rgba(59, 130, 246, 0.${alpha.toString().padStart(2, '0')})`;
  };

  // CSVエクスポート
  const exportToCsv = () => {
    if (!data || !matrixData) return;

    const rows: string[] = [];

    // ヘッダー行
    const header = [dimensionLabels[data.dimension1] || data.dimension1, ...data.columns, '合計'];
    rows.push(header.map((h) => `"${h}"`).join(','));

    // データ行
    for (const rowKey of data.rows) {
      const rowData = [
        rowKey,
        ...data.columns.map((col) => matrixData[rowKey]?.[col] || 0),
        data.totals.row_totals[rowKey] || 0,
      ];
      rows.push(rowData.map((d) => typeof d === 'string' ? `"${d}"` : d).join(','));
    }

    // 合計行
    const totalRow = [
      '合計',
      ...data.columns.map((col) => data.totals.col_totals[col] || 0),
      data.totals.grand_total,
    ];
    rows.push(totalRow.map((d) => typeof d === 'string' ? `"${d}"` : d).join(','));

    // ダウンロード
    const csv = '\uFEFF' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cross_analysis_${data.dimension1}_${data.dimension2}_${startDate}_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // 数値フォーマット
  const formatNumber = (value: number): string => {
    return value.toLocaleString('ja-JP', { maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">クロス分析</h1>
          <p className="text-sm text-gray-500 mt-1">
            2つの項目を掛け合わせて、データの関係性を表で確認できます
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowHelp(!showHelp)}
          className="text-gray-500 hover:text-gray-700"
        >
          <HelpCircle className="w-4 h-4 mr-1" />
          使い方
        </Button>
      </div>

      {/* 使い方ガイド */}
      {showHelp && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-3 text-sm text-blue-800">
              <div>
                <p className="font-bold mb-1">クロス分析とは？</p>
                <p>
                  2つの項目（例：「月」と「カテゴリ」）を縦軸・横軸にして表を作り、
                  <strong>どの組み合わせが多いか・少ないか</strong>を一目で確認できる機能です。
                </p>
              </div>
              <div>
                <p className="font-bold mb-1">こんなときに使えます</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>月ごとに登録数がどう変化しているか知りたい</li>
                  <li>LINEとメール、どちらの反応が良いか比較したい</li>
                  <li>どのシナリオ（流入経路）が効果的か確認したい</li>
                </ul>
              </div>
              <div>
                <p className="font-bold mb-1">使い方</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>下のプリセットから分析パターンを選ぶ（または自分で設定）</li>
                  <li>期間を選んで「分析実行」をクリック</li>
                  <li>表で結果を確認。数字が大きいセルは青く表示されます</li>
                </ol>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* 法人・期間選択 */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* 法人選択 */}
          {companyOptions.length > 1 ? (
            <Select
              label="法人"
              options={companyOptions}
              value={selectedCompanyId?.toString() || ''}
              onChange={(value) => setSelectedCompanyId(Number(value))}
            />
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
              <div className="px-3 py-2 bg-gray-100 rounded-md text-sm font-medium text-gray-900">
                {companyOptions[0]?.label || '-'}
              </div>
            </div>
          )}

          {/* 開始日 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>

          {/* 終了日 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">終了日</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>
        </div>
      </Card>

      {/* プリセットテンプレート */}
      <div>
        <h2 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
          <Zap className="w-4 h-4 mr-1 text-yellow-500" />
          分析パターンを選択（クリックで即実行）
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {presetTemplates.map((preset) => {
            const isSelected = dimension1 === preset.dimension1 &&
                              dimension2 === preset.dimension2 &&
                              metric === preset.metric;
            return (
              <button
                key={preset.id}
                onClick={() => applyPreset(preset)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`${isSelected ? 'text-primary' : 'text-gray-400'}`}>
                    {preset.icon}
                  </div>
                  <span className={`font-medium ${isSelected ? 'text-primary' : 'text-gray-900'}`}>
                    {preset.name}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{preset.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* カスタム設定 */}
      <Card className="p-4">
        <button
          onClick={() => setShowCustomSettings(!showCustomSettings)}
          className="w-full flex items-center justify-between text-sm font-medium text-gray-700"
        >
          <span className="flex items-center gap-2">
            <Table2 className="w-4 h-4" />
            自分で軸を選ぶ（カスタム設定）
          </span>
          {showCustomSettings ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>

        {showCustomSettings && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* 縦軸（行） */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  縦軸（表の行）
                </label>
                <select
                  value={dimension1}
                  onChange={(e) => setDimension1(e.target.value as CrossAnalysisDimension)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
                >
                  {dimensionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {dimensionOptions.find(o => o.value === dimension1)?.description}
                </p>
              </div>

              {/* 横軸（列） */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  横軸（表の列）
                </label>
                <select
                  value={dimension2}
                  onChange={(e) => setDimension2(e.target.value as CrossAnalysisDimension)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
                >
                  {dimension2Options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {dimensionOptions.find(o => o.value === dimension2)?.description}
                </p>
              </div>

              {/* 集計方法 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  集計方法
                </label>
                <select
                  value={metric}
                  onChange={(e) => setMetric(e.target.value as CrossAnalysisMetric)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
                >
                  {metricOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {metricOptions.find(o => o.value === metric)?.description}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 実行ボタン */}
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <Button onClick={() => fetchData()} disabled={loading} className="flex-1 sm:flex-none">
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                分析中...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                再分析
              </>
            )}
          </Button>

          {data && (
            <Button variant="outline" onClick={exportToCsv}>
              <Download className="w-4 h-4 mr-2" />
              CSV出力
            </Button>
          )}

          <span className="text-xs text-gray-500 ml-2">
            ※ 期間変更やカスタム設定後に押してください
          </span>
        </div>
      </Card>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* 結果テーブル */}
      {data && matrixData && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 className="font-semibold text-gray-900">
                  {dimensionLabels[data.dimension1]} × {dimensionLabels[data.dimension2]}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  期間: {data.period.start_date} 〜 {data.period.end_date}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Info className="w-4 h-4" />
                <span>数値が大きいセルほど濃い青色で表示</span>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                    {dimensionLabels[data.dimension1]}
                  </th>
                  {data.columns.map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider bg-gray-100 border-l border-gray-200">
                    合計
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.rows.map((row, rowIndex) => (
                  <tr key={row} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="sticky left-0 z-10 px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 bg-inherit">
                      {row}
                    </td>
                    {data.columns.map((col) => {
                      const value = matrixData[row]?.[col] || 0;
                      return (
                        <td
                          key={col}
                          className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right"
                          style={{ backgroundColor: getCellBackground(value) }}
                        >
                          {formatNumber(value)}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right bg-gray-100 border-l border-gray-200">
                      {formatNumber(data.totals.row_totals[row] || 0)}
                    </td>
                  </tr>
                ))}
                {/* 合計行 */}
                <tr className="bg-gray-100 font-medium">
                  <td className="sticky left-0 z-10 px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 bg-gray-100">
                    合計
                  </td>
                  {data.columns.map((col) => (
                    <td
                      key={col}
                      className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right"
                    >
                      {formatNumber(data.totals.col_totals[col] || 0)}
                    </td>
                  ))}
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right bg-gray-200 border-l border-gray-200">
                    {formatNumber(data.totals.grand_total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 結果の見方ガイド */}
          <div className="p-4 border-t border-gray-200 bg-yellow-50">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-yellow-800">
                <p className="font-medium mb-1">この表の見方</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>縦軸（行）: {dimensionLabels[data.dimension1]}、横軸（列）: {dimensionLabels[data.dimension2]}</li>
                  <li>青色が濃いセル = 数値が大きい = 注目ポイント</li>
                  <li>「合計」行・列で全体の傾向を確認できます</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* データなし */}
      {!loading && !error && !data && (
        <Card className="p-8 text-center text-gray-500">
          <Table2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="mb-2">上の分析パターンをクリックしてください</p>
          <p className="text-xs text-gray-400">
            クリックすると自動で分析が実行されます
          </p>
        </Card>
      )}
    </div>
  );
}
